import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { ApprovalStatus, ListingStatus, OfferStatus } from '@biyolink/shared';
import { tEnum } from '@/i18n';

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const tones: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  success: 'bg-primary-100 text-primary-900',
  warning: 'bg-amber-100 text-amber-900',
  danger: 'bg-red-100 text-red-900',
  info: 'bg-sky-100 text-sky-900',
};

interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}

export function Badge({ tone = 'neutral', children, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const approvalTones: Record<ApprovalStatus, BadgeTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
};

export function ApprovalBadge({ status }: { status: ApprovalStatus }) {
  return <Badge tone={approvalTones[status]}>{tEnum('approvalStatus', status)}</Badge>;
}

const listingTones: Record<ListingStatus, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING: 'warning',
  ACTIVE: 'success',
  PAUSED: 'neutral',
  REJECTED: 'danger',
  EXPIRED: 'neutral',
  DELETED: 'neutral',
};

export function ListingStatusBadge({ status }: { status: ListingStatus }) {
  return <Badge tone={listingTones[status]}>{tEnum('listingStatus', status)}</Badge>;
}

const offerTones: Record<OfferStatus, BadgeTone> = {
  PENDING: 'warning',
  COUNTERED: 'info',
  ACCEPTED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'neutral',
  CANCELLED: 'neutral',
};

export function OfferStatusBadge({ status }: { status: OfferStatus }) {
  return <Badge tone={offerTones[status]}>{tEnum('offerStatus', status)}</Badge>;
}
